import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ImportResidentsResult } from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import {
  parseSheet,
  parseWorkbook,
  SHEET_EXAMPLE,
  type ParsedSheet,
} from '../lib/sheet.js';
import { Alert, Button, Card, CardHeader, PlanBlock, cx } from './ui.js';
import { IconClose } from './icons.js';

/**
 * Importacao da lista de moradores.
 *
 * A tela e feita em torno de uma desconfianca legitima: ninguem cola 300 linhas
 * num campo e clica em "gravar" sem saber o que vai acontecer. Por isso o fluxo
 * tem dois passos — conferir e importar — e o primeiro passa pelo MESMO codigo
 * do servidor que o segundo, com `dryRun`. O que a previa mostra e o que vai
 * ser gravado, e nao uma estimativa parecida feita no cliente.
 *
 * O arquivo e lido aqui no navegador (ver `lib/sheet.ts`): a contagem de linhas
 * e os erros de formato aparecem antes de qualquer requisicao.
 */
export function ImportResidentsCard({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState('');
  const [createMissing, setCreateMissing] = useState(true);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedFile, setParsedFile] = useState<ParsedSheet | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [preview, setPreview] = useState<ImportResidentsResult | null>(null);
  const [done, setDone] = useState<ImportResidentsResult | null>(null);
  const [error, setError] = useState<ApiRequestError | null>(null);

  const pastedSheet = useMemo(() => parseSheet(text), [text]);
  const sheet = parsedFile ?? pastedSheet;

  function send(dryRun: boolean) {
    return api.post<ImportResidentsResult>('/residents/import', {
      rows: sheet.rows,
      sourceRows: sheet.sourceRows,
      ignoredRows: sheet.ignoredRows,
      createMissing,
      dryRun,
    });
  }

  function fail(cause: unknown) {
    if (cause instanceof ApiRequestError) {
      setError(cause);
      setFileError(null);
      return;
    }
    setError(null);
    setFileError(cause instanceof Error ? cause.message : 'Nao foi possivel concluir a importacao.');
  }

  const check = useMutation({
    mutationFn: () => send(true),
    onSuccess: (result) => {
      setPreview(result);
      setError(null);
      setFileError(null);
    },
    onError: fail,
  });

  const commit = useMutation({
    mutationFn: () => send(false),
    onSuccess: (result) => {
      setDone(result);
      setPreview(null);
      setError(null);
      setFileError(null);
      // A lista, os contadores e o seletor de unidade do registro de encomenda
      // ficaram velhos no mesmo instante.
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
      void queryClient.invalidateQueries({ queryKey: ['blocks'] });
    },
    onError: fail,
  });

  async function load(file: File) {
    setFileName(file.name);
    setFileError(null);
    setError(null);
    setLoadingFile(true);
    setPreview(null);
    setDone(null);
    // CSV moderno fica em UTF-8; o fallback abaixo preserva exportacoes legadas
    // do Excel em Windows-1252 sem corromper os arquivos que vieram corretos.
    try {
      const data = await file.arrayBuffer();
      if (/\.xlsx?$/i.test(file.name)) {
        setText('');
        setParsedFile(await parseWorkbook(data));
      } else {
        let decoded: string;
        try {
          decoded = new TextDecoder('utf-8', { fatal: true }).decode(data);
        } catch {
          // CSV legado exportado pelo Excel em portugues costuma ser Windows-1252.
          decoded = new TextDecoder('windows-1252').decode(data);
        }
        setParsedFile(null);
        setText(decoded);
      }
    } catch (cause) {
      setParsedFile(null);
      setText('');
      setFileError(
        cause instanceof Error
          ? `Nao foi possivel ler o arquivo: ${cause.message}`
          : 'Nao foi possivel ler o arquivo selecionado.',
      );
    } finally {
      setLoadingFile(false);
    }
  }

  return (
    <Card className="mb-4 border-accent/30">
      <CardHeader
        title="Importar lista de moradores"
        subtitle="A planilha do condominio inteira de uma vez — com previa antes de gravar."
        action={
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar importacao"
            className="rounded-xl p-2 text-faint transition hover:bg-surface-2 hover:text-fg"
          >
            <IconClose className="size-5" />
          </button>
        }
      />

      <div className="space-y-4 px-5 pb-5 sm:px-6">
        {error?.isPlanBlock ? (
          <PlanBlock message={error.message} />
        ) : (
          error && <Alert>{error.message}</Alert>
        )}
        {fileError && <Alert>{fileError}</Alert>}

        {done ? (
          <ImportDone report={done} onAgain={() => setDone(null)} onClose={onClose} />
        ) : (
          <>
            <div className="rounded-2xl border border-line bg-surface-2 p-4 text-sm">
              <p className="font-semibold">
                Envie XLS/XLSX, CSV ou cole as celulas direto do Excel. A primeira linha e
                o cabecalho.
              </p>
              <p className="mt-2 text-muted">
                Colunas reconhecidas: <code className="text-fg">bloco</code>,{' '}
                <code className="text-fg">unidade</code>, <code className="text-fg">nome</code>,{' '}
                <code className="text-fg">cpf</code>, <code className="text-fg">whatsapp</code>,{' '}
                <code className="text-fg">email</code>, <code className="text-fg">vinculo</code>{' '}
                e grupos de proprietario/locatario. Unidade e nome identificam cada pessoa.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  className="py-2"
                  disabled={check.isPending || commit.isPending || loadingFile}
                  onClick={() => {
                    setText(SHEET_EXAMPLE);
                    setParsedFile(null);
                    setFileName(null);
                    setFileError(null);
                    setError(null);
                    setPreview(null);
                  }}
                >
                  Usar exemplo
                </Button>
                <Button
                  variant="ghost"
                  className="py-2"
                  loading={loadingFile}
                  disabled={check.isPending || commit.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  Escolher arquivo
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xls,.xlsx,.csv,.txt,.tsv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void load(file);
                    // Permite escolher o MESMO arquivo de novo depois de ajustar.
                    event.target.value = '';
                  }}
                />
                {fileName && (
                  <span aria-live="polite" className="self-center text-xs text-faint">
                    {fileName}
                  </span>
                )}
              </div>
            </div>

            <textarea
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setParsedFile(null);
                setFileName(null);
                setFileError(null);
                setError(null);
                setPreview(null);
              }}
              rows={8}
              disabled={check.isPending || commit.isPending || loadingFile}
              spellCheck={false}
              placeholder={
                parsedFile ? 'Arquivo Excel carregado e pronto para conferencia.' : SHEET_EXAMPLE
              }
              aria-label="Planilha de moradores"
              className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 font-mono text-xs text-fg placeholder:text-faint focus:border-accent/60 focus:outline-none"
            />

            {sheet.error && <Alert tone="warn">{sheet.error}</Alert>}

            {sheet.rows.length > 0 && (
              <p className="text-sm text-muted">
                <span className="font-bold text-fg tabular-nums">{sheet.sourceRows}</span>{' '}
                {sheet.sourceRows === 1 ? 'linha de dados lida' : 'linhas de dados lidas'}
                {' • '}
                <span className="font-bold text-fg tabular-nums">{sheet.rows.length}</span>{' '}
                {sheet.rows.length === 1
                  ? 'pessoa/unidade reconhecida'
                  : 'pessoas/unidades reconhecidas'}
                {sheet.ignored.length > 0 && (
                  <span className="text-faint">
                    {' '}
                    • colunas ignoradas: {sheet.ignored.join(', ')}
                  </span>
                )}
              </p>
            )}

            {/* O parser toma decisoes sozinho na planilha da administradora —
                descarta garagem, reconhece "Unidade Vazia", tira o prefixo do
                bloco. Todas defensaveis, nenhuma silenciosa: quem confere a
                lista do predio precisa ver o que foi mexido antes de gravar. */}
            {sheet.notes.length > 0 && (
              <ul className="space-y-1 text-sm text-muted">
                {sheet.notes.map((note) => (
                  <li key={note}>• {note}</li>
                ))}
              </ul>
            )}

            <label className="flex items-center gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={createMissing}
                disabled={check.isPending || commit.isPending || loadingFile}
                onChange={(event) => {
                  setCreateMissing(event.target.checked);
                  setPreview(null);
                }}
                className="size-4 accent-accent"
              />
              Criar blocos e unidades que ainda nao existem
            </label>

            {preview && <ImportReport report={preview} />}

            <div className="flex flex-wrap gap-3">
              <Button
                variant={preview ? 'ghost' : 'primary'}
                loading={check.isPending}
                disabled={sheet.rows.length === 0}
                onClick={() => check.mutate()}
              >
                Conferir
              </Button>

              {/* So aparece depois da conferencia: gravar sem ver o relatorio e
                  exatamente o que assusta quem tem a lista do predio na mao. */}
              {preview && (
                <Button
                  loading={commit.isPending}
                  disabled={!reportHasChanges(preview)}
                  onClick={() => commit.mutate()}
                >
                  Aplicar alteracoes
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function reportHasChanges(report: ImportResidentsResult): boolean {
  return (
    report.blocksCreated.length > 0 ||
    report.unitsCreated.length > 0 ||
    report.residentsCreated > 0 ||
    report.residentsUpdated > 0 ||
    report.residentUnitLinksCreated > 0 ||
    report.residentUnitLinksUpdated > 0 ||
    report.ownershipLinksUpdated > 0
  );
}

function Line({ label, value, tone }: { label: string; value: number; tone?: 'accent' | 'warn' }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted">{label}</span>
      <span
        className={cx(
          'font-bold tabular-nums',
          tone === 'accent' ? 'text-accent' : tone === 'warn' ? 'text-warn' : 'text-fg',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function ImportReport({
  report,
  title = 'O que vai acontecer',
}: {
  report: ImportResidentsResult;
  title?: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-surface-2 p-4">
      <p className="eyebrow">{title}</p>

      <div className="mt-3 grid gap-x-8 gap-y-2 sm:grid-cols-2">
        <Line label="Linhas processadas" value={report.rows} />
        <Line
          label="Pessoas novas (inclui proprietarios)"
          value={report.residentsCreated}
          tone="accent"
        />
        <Line label="Pessoas atualizadas" value={report.residentsUpdated} />
        <Line label="Proprietarios novos" value={report.ownersCreated} tone="accent" />
        <Line label="Proprietarios atualizados" value={report.ownersUpdated} />
        <Line label="Vinculos pessoa/unidade novos" value={report.residentUnitLinksCreated} />
        <Line
          label="Vinculos pessoa/unidade atualizados"
          value={report.residentUnitLinksUpdated}
        />
        <Line label="Vinculos de propriedade novos" value={report.ownershipLinksCreated} />
        <Line label="Vinculos promovidos a proprietario" value={report.ownershipLinksUpdated} />
        <Line label="Unidades vazias com proprietario" value={report.vacantUnitsAssigned} />
        <Line label="Unidades novas" value={report.unitsCreated.length} />
        <Line label="Blocos novos" value={report.blocksCreated.length} />
        <Line label="Pessoas ja vinculadas (sem alteracao)" value={report.residentsSkipped} />
        <Line label="Registros ignorados (total)" value={report.recordsIgnored} />
        <Line
          label="Sem telefone"
          value={report.residentsWithoutPhone}
          tone={report.residentsWithoutPhone > 0 ? 'warn' : undefined}
        />
      </div>

      {report.blocksCreated.length > 0 && (
        <p className="mt-3 text-sm text-muted">
          Blocos: <span className="text-fg">{report.blocksCreated.join(', ')}</span>
        </p>
      )}

      {/* O numero que decide se o condominio pode comecar a operar. Morador sem
          telefone nao e erro de planilha — a lista da administradora vem assim
          —, mas unidade onde NINGUEM tem telefone nem e-mail e uma unidade cuja
          encomenda a portaria registra e ninguem fica sabendo. Precisa aparecer
          antes de gravar, e nao virar reclamacao na primeira semana de uso. */}
      {report.unitsWithoutContact.length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-sm font-bold text-warn">
            {report.unitsWithoutContact.length}{' '}
            {report.unitsWithoutContact.length === 1
              ? 'unidade fica sem ninguem para avisar'
              : 'unidades ficam sem ninguem para avisar'}
          </p>
          <p className="mt-1 text-sm text-muted">
            Nenhum morador com WhatsApp nem e-mail. A encomenda e registrada, mas o aviso nao sai —
            peca o contato dessas unidades a administradora.
          </p>
          <p className="mt-2 max-h-24 overflow-y-auto text-sm text-fg">
            {report.unitsWithoutContact.join(', ')}
          </p>
        </div>
      )}

      {report.errors.length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-sm font-bold text-warn">
            {report.errors.length}{' '}
            {report.errors.length === 1 ? 'problema encontrado' : 'problemas encontrados'} — os
            registros validos entram normalmente.
          </p>
          <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-sm">
            {report.errors.map((item, index) => (
              <li key={`${item.line}-${index}`} className="text-muted">
                <span className="font-semibold text-fg">Linha {item.line}:</span> {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ImportDone({
  report,
  onAgain,
  onClose,
}: {
  report: ImportResidentsResult;
  onAgain: () => void;
  onClose: () => void;
}) {
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
      <p className="eyebrow">Pronto</p>
      <h3 className="mt-2 text-xl font-bold tracking-tight">Importacao concluida</h3>
      <p className="mt-2 text-sm text-muted">
        {report.rows} linha(s) processada(s). Os registros validos foram aplicados e os problemas
        individuais permaneceram identificados pela linha da planilha.
      </p>

      <div className="mt-4">
        <ImportReport report={report} title="Resumo da importacao" />
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button onClick={onClose}>Ver a lista</Button>
        <Button variant="ghost" onClick={onAgain}>
          Importar outra lista
        </Button>
      </div>
    </div>
  );
}
